// based on https://github.com/antonfisher/react-simple-timefield but allows empty field value and 24:00 time
import React, {
  ComponentPropsWithoutRef,
  ElementType,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import Input from '../input/Input';

const DEFAULT_VALUE = '00:00';

const isNumber = (value) => {
  const number = Number(value);
  return !isNaN(number) && String(value) === String(number);
};

const formatTimeItem = (value) => {
  return `${value || ''}00`.substr(0, 2);
};

const validateTimeAndCursor = (
  value = '',
  oldValue = '',
  cursorPosition = 0,
): [string, number] => {
  const [oldH, oldM] = oldValue.split(':');

  let newCursorPosition = Number(cursorPosition);
  let [newH, newM] = String(value).split(':');

  newH = formatTimeItem(newH);
  if (Number(newH[0]) > 2) {
    newH = oldH;
    newCursorPosition -= 1;
  } else if (Number(newH[0]) === 2) {
    if (Number(oldH[0]) === 2 && Number(newH[1]) > 4) {
      newH = `2${oldH[1]}`;
      newCursorPosition -= 2;
    } else if (Number(newH[1]) > 4) {
      newH = '24';
    }
  }

  newM = formatTimeItem(newM);
  if (Number(newM[0]) > 5) {
    newM = oldM;
    newCursorPosition -= 1;
  } else if (Number(newH) === 24) {
    newM = '00';
  }

  const validatedValue = `${newH}:${newM}`;

  return [validatedValue, newCursorPosition];
};

const getNewValueAndPosition = (currentValue, oldValue, position) => {
  position = position || 0;
  const isTyped = currentValue.length > oldValue.length;
  const cursorCharacter = currentValue[position - 1];
  const addedCharacter = isTyped ? cursorCharacter : null;
  const removedCharacter = isTyped ? null : oldValue[position];
  const replacedSingleCharacter =
    currentValue.length === oldValue.length ? oldValue[position - 1] : null;

  let newValue = oldValue;
  let newPosition = position;

  if (addedCharacter !== null) {
    if (position > DEFAULT_VALUE.length) {
      newPosition = DEFAULT_VALUE.length;
    } else if ((position === 3 || position === 6) && addedCharacter === ':') {
      newValue = `${currentValue.substr(0, position - 1)}:${currentValue.substr(
        position + 1,
      )}`;
    } else if ((position === 3 || position === 6) && isNumber(addedCharacter)) {
      newValue = `${currentValue.substr(
        0,
        position - 1,
      )}:${addedCharacter}${currentValue.substr(position + 2)}`;
      newPosition = position + 1;
    } else if (isNumber(addedCharacter)) {
      // user typed a number
      newValue =
        currentValue.substr(0, position - 1) +
        addedCharacter +
        currentValue.substr(position + 1);
      if (position === 2 || position === 5) {
        newPosition = position + 1;
      }
    } else {
      // if user typed NOT a number, then keep old value & position
      newPosition = position - 1;
    }
  } else if (replacedSingleCharacter !== null) {
    // user replaced only a single character
    if (isNumber(cursorCharacter)) {
      if (position - 1 === 2 || position - 1 === 5) {
        newValue = `${currentValue.substr(
          0,
          position - 1,
        )}:${currentValue.substr(position)}`;
      } else {
        newValue = currentValue;
      }
    } else {
      // user replaced a number on some non-number character
      newValue = oldValue;
      newPosition = position - 1;
    }
  } else if (
    typeof cursorCharacter !== 'undefined' &&
    cursorCharacter !== ':' &&
    !isNumber(cursorCharacter)
  ) {
    // set of characters replaced by non-number
    newValue = oldValue;
    newPosition = position - 1;
  } else if (removedCharacter !== null) {
    if ((position === 2 || position === 5) && removedCharacter === ':') {
      newValue = `${currentValue.substr(
        0,
        position - 1,
      )}0:${currentValue.substr(position)}`;
      newPosition = position - 1;
    } else {
      // user removed a number
      newValue = `${currentValue.substr(0, position)}0${currentValue.substr(
        position,
      )}`;
    }
  }

  return [newValue, newPosition];
};

export interface TimeInputProps extends ComponentPropsWithoutRef<ElementType> {
  value?;
  onChange?;
}

const TimeInput = ({ value, onChange, ...props }: TimeInputProps) => {
  const inputRef = useRef<{ selectionStart; selectionEnd }>();

  const [innerValue, setInnerValue] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);

  useEffect(() => {
    if (value) {
      const [validatedTime] = validateTimeAndCursor(value, DEFAULT_VALUE);
      setInnerValue(validatedTime);
    } else {
      setInnerValue(value || '');
    }
  }, [value]);

  useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.selectionStart = cursorPosition;
      inputRef.current.selectionEnd = cursorPosition;
    }
  }, [cursorPosition]);

  const handleOnChange = useCallback(
    (event) => {
      const oldValue = innerValue;
      const inputEl = event.target;
      const currentValue = inputEl.value || '';

      if (currentValue) {
        const [newValue, newPosition] = getNewValueAndPosition(
          currentValue,
          oldValue,
          inputEl.selectionEnd,
        );

        const [validatedTime, validatedCursorPosition] = validateTimeAndCursor(
          newValue,
          oldValue,
          newPosition,
        );

        setInnerValue(validatedTime);
        setCursorPosition(validatedCursorPosition);

        if (onChange) {
          onChange(event, validatedTime);
        }
      } else {
        setInnerValue(currentValue);
        if (onChange) {
          onChange(event, currentValue);
        }
      }
    },
    [innerValue, onChange],
  );

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      placeholder="00:00"
      value={innerValue}
      onChange={handleOnChange}
    />
  );
};

export default TimeInput;
